import type { QuotePlanParameters } from "../types";

/**
 * Pure payment schedule calculator (Almikia Epic EQ). One function renders the
 * advisor preview, the saved quote and the public page, so the schedule is
 * derived from a frozen unit price + a frozen plan snapshot and never stored.
 *
 * The plan is driven only by the five percentages and two counts (the
 * `plan_type` label is irrelevant here). The `discount_pct` of a plan is
 * informational: callers pass the price they want the schedule to add up to.
 */

export type ScheduleConcept =
  | "enganche"
  | "mensualidad"
  | "diferido"
  | "entrega"
  | "credito";

export type ScheduleRow = {
  concept: ScheduleConcept;
  /** 1-based position of the payment within its concept. */
  number: number;
  /** Months after the quote date (0 = at signing); null when it has no fixed date. */
  monthOffset: number | null;
  /** Share of the price this single payment carries, in percent. */
  percentage: number;
  /** Amount in currency units, rounded to cents. */
  amount: number;
};

/** Thrown when a plan or a price cannot produce a valid schedule. */
export class PaymentPlanError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PaymentPlanError";
  }
}

export const PLAN_PERCENTAGES_MUST_SUM_100_MESSAGE =
  "Los porcentajes del plan deben sumar 100";
const INVALID_PRICE_MESSAGE = "El precio de la unidad no es válido";
const INVALID_PLAN_MESSAGE = "El plan de pago tiene valores no válidos";
const MISSING_COUNT_MESSAGE =
  "Una parte del plan con porcentaje necesita al menos un pago";

const PERCENT_TOLERANCE = 1e-6;

const isFiniteNonNegative = (value: number) =>
  Number.isFinite(value) && value >= 0;

const isNonNegativeInteger = (value: number) =>
  Number.isInteger(value) && value >= 0;

type Part = {
  concept: ScheduleConcept;
  pct: number;
  count: number;
  /** First `monthOffset`, or null when the part has no fixed date. */
  startOffset: number | null;
};

const assertValidPlan = (plan: QuotePlanParameters) => {
  const percentages = [
    plan.down_payment_pct,
    plan.monthly_payments_pct,
    plan.deferred_payments_pct,
    plan.delivery_pct,
    plan.credit_pct,
  ];
  const counts = [
    plan.down_payment_count,
    plan.monthly_payments_count,
    plan.deferred_payments_count,
  ];
  if (
    !percentages.every(isFiniteNonNegative) ||
    !counts.every(isNonNegativeInteger)
  ) {
    throw new PaymentPlanError(INVALID_PLAN_MESSAGE);
  }
  const total = percentages.reduce((sum, pct) => sum + pct, 0);
  if (Math.abs(total - 100) > PERCENT_TOLERANCE) {
    throw new PaymentPlanError(PLAN_PERCENTAGES_MUST_SUM_100_MESSAGE);
  }
  if (
    (plan.monthly_payments_pct > 0 && plan.monthly_payments_count === 0) ||
    (plan.deferred_payments_pct > 0 && plan.deferred_payments_count === 0)
  ) {
    throw new PaymentPlanError(MISSING_COUNT_MESSAGE);
  }
};

/**
 * Builds the ordered payment schedule for a frozen unit price and plan.
 *
 * Order: down payment (months 0..n-1), monthly payments (consecutive months),
 * deferred payments (consecutive months after the monthlies), payment on
 * delivery and credit (no fixed month). A down payment with a share but a
 * zero count is a single payment. Amounts are computed in cents from
 * cumulative percentages, so they are never negative and the LAST installment
 * of each part absorbs its rounding: the rows always sum exactly to the price.
 *
 * @throws PaymentPlanError when the price or plan is invalid, or the
 * percentages do not sum to 100.
 */
export const calculatePaymentSchedule = (
  unitPrice: number,
  plan: QuotePlanParameters,
): ScheduleRow[] => {
  if (!isFiniteNonNegative(unitPrice)) {
    throw new PaymentPlanError(INVALID_PRICE_MESSAGE);
  }
  assertValidPlan(plan);

  const downCount =
    plan.down_payment_pct > 0 ? Math.max(plan.down_payment_count, 1) : 0;
  const monthlyStart = Math.max(downCount, 1);
  const deferredStart = monthlyStart + plan.monthly_payments_count;

  const parts: Part[] = [
    {
      concept: "enganche",
      pct: plan.down_payment_pct,
      count: downCount,
      startOffset: 0,
    },
    {
      concept: "mensualidad",
      pct: plan.monthly_payments_pct,
      count: plan.monthly_payments_count,
      startOffset: monthlyStart,
    },
    {
      concept: "diferido",
      pct: plan.deferred_payments_pct,
      count: plan.deferred_payments_count,
      startOffset: deferredStart,
    },
    {
      concept: "entrega",
      pct: plan.delivery_pct,
      count: plan.delivery_pct > 0 ? 1 : 0,
      startOffset: null,
    },
    {
      concept: "credito",
      pct: plan.credit_pct,
      count: plan.credit_pct > 0 ? 1 : 0,
      startOffset: null,
    },
  ];
  const activeParts = parts.filter((part) => part.count > 0);
  const totalCents = Math.round(unitPrice * 100);

  const rows: ScheduleRow[] = [];
  let cumulativePct = 0;
  let previousCumulativeCents = 0;
  activeParts.forEach((part, partIndex) => {
    cumulativePct += part.pct;
    const isLastPart = partIndex === activeParts.length - 1;
    const cumulativeCents = isLastPart
      ? totalCents
      : Math.round((totalCents * cumulativePct) / 100);
    const partCents = cumulativeCents - previousCumulativeCents;
    previousCumulativeCents = cumulativeCents;

    const baseCents = Math.floor(partCents / part.count);
    const rowPct = Math.round((part.pct / part.count) * 1e6) / 1e6;
    for (let index = 0; index < part.count; index += 1) {
      const isLastInstallment = index === part.count - 1;
      const cents = isLastInstallment
        ? partCents - baseCents * (part.count - 1)
        : baseCents;
      rows.push({
        concept: part.concept,
        number: index + 1,
        monthOffset:
          part.startOffset === null ? null : part.startOffset + index,
        percentage: rowPct,
        amount: cents / 100,
      });
    }
  });
  return rows;
};
